import { EMR_PAGE_SOURCE_LABELS } from "@emr/contracts";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { holdCapture } from "./captureIntake";
import {
  acknowledgeCapture,
  announceReady,
  captureFromMessage,
  type EmrPageCapture,
  type RelayChannelFactory,
  relayCaptureToWaitingTab,
} from "./handoff";
import "../patient-profile.css";
import "./emr-import.css";

type Stage =
  | { readonly kind: "receiving" }
  | { readonly kind: "relayed" }
  | { readonly kind: "unclaimed"; readonly capture: EmrPageCapture };

/**
 * Where the "Send to BobDoc" bookmark lands (EXECDEV-227), before any sign-in.
 *
 * Workforce sessions live in memory, one per tab, so the tab the bookmark
 * opens is never signed in. It does not need to be: it tells the other EMR's
 * page it is ready, takes the chart that page posts, and hands it in memory to
 * the BobDoc tab already waiting with an import box open (signed in there),
 * then closes. Nothing here calls the server. When no tab is waiting, the
 * physician can register the patient in this tab instead, signing in first;
 * the chart waits in memory meanwhile.
 */
export function EmrPageRelay({
  enabled,
  allowLoopback,
  opener,
  relayChannel,
  onRegisterHere,
}: {
  readonly enabled: boolean;
  /** Accept a chart page served from loopback (local development only). */
  readonly allowLoopback: boolean;
  readonly opener: Pick<Window, "postMessage"> | null;
  readonly relayChannel?: RelayChannelFactory | undefined;
  readonly onRegisterHere: () => void;
}) {
  const [stage, setStage] = useState<Stage>({ kind: "receiving" });
  const accepted = useRef(false);
  const label = EMR_PAGE_SOURCE_LABELS["telus-chr"];

  useEffect(() => {
    if (!enabled || opener === null) return;
    const listen = (event: MessageEvent) => {
      if (event.source !== opener || accepted.current) return;
      const capture = captureFromMessage(event, { allowLoopback });
      if (capture === null) return;
      accepted.current = true;
      acknowledgeCapture(opener, event.origin);
      void relayCaptureToWaitingTab(capture, { channel: relayChannel }).then((relayed) => {
        if (relayed) {
          setStage({ kind: "relayed" });
          window.close();
        } else {
          setStage({ kind: "unclaimed", capture });
        }
      });
    };
    window.addEventListener("message", listen);
    // The other page may still be wiring its listener; say so until it answers.
    announceReady(opener);
    const repeat = window.setInterval(() => {
      if (!accepted.current) announceReady(opener);
    }, 1000);
    return () => {
      window.removeEventListener("message", listen);
      window.clearInterval(repeat);
    };
  }, [enabled, allowLoopback, opener, relayChannel]);

  let body: ReactNode;
  if (!enabled) {
    body = (
      <p role="alert">Registering a patient from another EMR's chart is not available here.</p>
    );
  } else if (opener === null) {
    body = (
      <p role="status">
        Open a patient's chart in {label} and click <strong>Send to BobDoc</strong> in your
        bookmarks bar. To install it, go to Patients, choose Register patient, then Import from{" "}
        {label}.
      </p>
    );
  } else if (stage.kind === "relayed") {
    body = <p role="status">Sent to your BobDoc tab. You can close this tab.</p>;
  } else if (stage.kind === "unclaimed") {
    const { capture } = stage;
    body = (
      <section className="pp-import" aria-labelledby="emr-page-relay-unclaimed">
        <p className="pp-subhead" id="emr-page-relay-unclaimed">
          No BobDoc tab is waiting for this chart
        </p>
        <p>
          In the BobDoc tab you are working in, open <strong>Register patient</strong> on Patients
          or <strong>Add walk-in</strong> on Today, choose <strong>Import from {label}</strong>,
          then click Send to BobDoc again. Or register the patient in this tab; you will sign in
          first.
        </p>
        <div className="pp-editor-actions">
          <button
            type="button"
            className="pp-btn primary"
            onClick={() => {
              holdCapture(capture);
              onRegisterHere();
            }}
          >
            Register the patient here
          </button>
        </div>
      </section>
    );
  } else {
    body = <p role="status">Receiving the chart from {label}…</p>;
  }

  return (
    <main className="pp-page pp-relay-page" aria-labelledby="emr-page-relay-title">
      <div className="pp-relay">
        <p className="pp-eyebrow">Bob Doc</p>
        <h1 id="emr-page-relay-title">Import from {label}</h1>
        {body}
      </div>
    </main>
  );
}
